import { observer } from "mobx-react-lite";
import React from "react";
import { Box } from "src/components/Box";
import { Button } from "src/components/Button";
import { Modal, ModalContent, ModalFooter } from "src/components/Modal";
import { Text } from "src/components/Text";
import { useTranslation } from "src/hooks";
import styled from "styled-components";
import { MediaQueries } from "src/constants";
import { Briefcase } from "@asphalt-react/iconpack";
import { IdNumber } from "@asphalt-react/iconpack";
import { Document } from "@asphalt-react/iconpack";
import { useHistory } from "react-router-dom";
import { usePassportStore } from "src/stores/Passport.store";
import { PASSPORT_ROUTES } from "src/constants/routes";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { Logger } from "src/helpers/logger";
import { errFormatter } from "src/helpers/errorFormatter";
import { setCookie, CookieNames } from "src/helpers/cookies";
import { useErrorModalStore } from "src/components/ErrorModal/ErrorModal.store";

const ButtonEl = styled(Button)`
  ${MediaQueries.screen.medium.minWidth} {
    min-width: 120px;
  }
`;

const CardDocumentTitleEl = styled(Text)`
  ${MediaQueries.screen.medium.minWidth} {
    font-size: ${(p) => p.theme.text.size.normal};
    margin-bottom: ${(p) => p.theme.layout.margin.s}px;
  }
`;

const ContainerEl = styled.div`
  ${MediaQueries.screen.medium.minWidth} {
    padding: ${(p) => p.theme.layout.margin.s}px
      ${(p) => p.theme.layout.margin.s}px 0;
  }
`;

const DocumentCardEl = styled(Box)`
  border: 1px solid rgb(242, 242, 242);
  padding: ${(p) => p.theme.layout.margin.l}px;
  margin-bottom: ${(p) => p.theme.layout.margin.l}px;

  ${MediaQueries.screen.medium.minWidth} {
    min-width: 392px;
    max-width: 392px;
    :first-child {
      margin-right: 24px;
    }
  }
`;

const DocumentContainerEl = styled.div`
  ${MediaQueries.screen.medium.minWidth} {
    display: flex;
  }
`;

const IconContainerEl = styled.div`
  svg {
    min-width: 30px;
  }
  path {
    fill: ${(p) => p.theme.colors.borders.brand};
  }
  ${MediaQueries.screen.medium.minWidth} {
    svg {
      min-width: 36px;
    }
  }
`;

const SectionTitleEl = styled(Text)`
  ${MediaQueries.screen.medium.minWidth} {
    font-size: ${(p) => p.theme.text.size.large};
  }
`;

const TextContainerEl = styled.div`
  margin-left: ${(p) => p.theme.layout.margin.l}px;
  ${MediaQueries.screen.medium.minWidth} {
    margin-left: ${(p) => p.theme.layout.margin.xl}px;
  }
`;

export const PaymentMethodModal = observer(() => {
  const { push } = useHistory();
  const translate = useTranslation();
  const passportStore = usePassportStore();
  const {
    selectedPaymentMethod,
    selectedBusinessType,
    loadingState,
  } = passportStore;
  const errorModalStore = useErrorModalStore();

  const _onSubmit = async () => {
    eventDispatcher.publish({
      action: "ob_payment_next",
      payload: {
        customer_id: passportStore.authUser.merchantId,
        email: passportStore.authUser.email,
        current_time: new Date().toISOString(),
      },
    });
    try {
      await passportStore.updateOutlet({
        metadata: {
          midtrans_credit_card_optin: selectedPaymentMethod === "card",
        },
      });
      setCookie(CookieNames.PassportStep, PASSPORT_ROUTES.ownerInfo);
      push(PASSPORT_ROUTES.ownerInfo);
    } catch (err) {
      /**
       * TODO: Implement error for network offline and server errors when refactored
       * @date - 22/10/20
       * @time - 17.10
       */
      Logger.error(err);
      const { status, message } = errFormatter(err);
      eventDispatcher.publish({
        action: "ob_payment_error",
        payload: {
          message: message,
          httpStatus: status,
        },
      });
      errorModalStore.showAlert();
      errorModalStore.retryCta = _onSubmit;
    }
  };

  const isLoading = loadingState.has.updateOutlet;

  return (
    <Modal fitWidth height={1}>
      <ModalContent>
        <ContainerEl>
          <SectionTitleEl fontWeight="demi" marginBottomM>
            {translate("payment_methods.prepare_document.title")}
          </SectionTitleEl>
          <Text fontSize="medium" marginBottomXl>
            {translate("payment_methods.prepare_document.description")}
          </Text>
          {selectedBusinessType === "corporate" && (
            <SectionTitleEl fontWeight="demi" marginBottomL>
              {translate("payment_methods.owner_data")}
            </SectionTitleEl>
          )}
          <DocumentContainerEl>
            <DocumentCardEl flex>
              <IconContainerEl>
                <IdNumber />
              </IconContainerEl>
              <TextContainerEl>
                <CardDocumentTitleEl fontSize="medium" fontWeight="demi">
                  {translate("payment_methods.owner_identity_card.title")}
                </CardDocumentTitleEl>
                <Text fontSize="medium">
                  {translate("payment_methods.owner_identity_card.description")}
                </Text>
              </TextContainerEl>
            </DocumentCardEl>
            {selectedBusinessType === "individual" &&
              selectedPaymentMethod === "card" && (
                <DocumentCardEl flex>
                  <IconContainerEl>
                    <Briefcase />
                  </IconContainerEl>
                  <TextContainerEl>
                    <CardDocumentTitleEl fontSize="medium" fontWeight="demi">
                      {translate("payment_methods.owner_npwp.title")}
                    </CardDocumentTitleEl>
                    <Text fontSize="medium">
                      {translate("payment_methods.owner_npwp.description")}
                    </Text>
                  </TextContainerEl>
                </DocumentCardEl>
              )}
          </DocumentContainerEl>
          {selectedBusinessType === "corporate" && (
            <>
              <SectionTitleEl fontWeight="demi" marginBottomL marginTopXl>
                {translate("payment_methods.business_data")}
              </SectionTitleEl>
              <DocumentContainerEl>
                <DocumentCardEl flex>
                  <IconContainerEl>
                    <Briefcase />
                  </IconContainerEl>
                  <TextContainerEl>
                    <CardDocumentTitleEl fontSize="medium" fontWeight="demi">
                      {translate("payment_methods.business_npwp.title")}
                    </CardDocumentTitleEl>
                    <Text fontSize="medium">
                      {translate("payment_methods.business_npwp.description")}
                    </Text>
                  </TextContainerEl>
                </DocumentCardEl>
                <DocumentCardEl flex marginBottomL>
                  <IconContainerEl>
                    <Document />
                  </IconContainerEl>
                  <TextContainerEl>
                    <CardDocumentTitleEl fontSize="medium" fontWeight="demi">
                      {translate("payment_methods.akta.title")}
                    </CardDocumentTitleEl>
                    <Text fontSize="medium">
                      {translate("payment_methods.akta.description")}
                    </Text>
                  </TextContainerEl>
                </DocumentCardEl>
              </DocumentContainerEl>
              <DocumentCardEl flex>
                <IconContainerEl>
                  <Document />
                </IconContainerEl>
                <TextContainerEl>
                  <CardDocumentTitleEl fontSize="medium" fontWeight="demi">
                    {translate("payment_methods.sk_menkeh.title")}
                  </CardDocumentTitleEl>
                  <Text fontSize="medium">
                    {translate("payment_methods.sk_menkeh.description")}
                  </Text>
                </TextContainerEl>
              </DocumentCardEl>
            </>
          )}
        </ContainerEl>
      </ModalContent>
      <ContainerEl style={{ marginTop: "-24px" }}>
        <ModalFooter>
          <ButtonEl onClick={_onSubmit} loading={isLoading}>
            {translate("payment_methods.cta.ok")}
          </ButtonEl>
        </ModalFooter>
      </ContainerEl>
    </Modal>
  );
});
